import {
  type BridgeTarget,
  // getStargateTargetConfig,
  // getStargateConfigByChainId,
  getBridgeTargetConfig,
  DepositSource,
} from '@katanaperps/katana-perps-sdk';

import config from 'config';
import { ChainsFromConfig } from 'config/configType';
import {
  BANKROLL_BRIDGE_SOURCE,
  COMPETITION_REWARD_BRIDGE_SOURCE,
  POINTS_REWARD_BRIDGE_SOURCE,
  REWARDS_BRIDGE_SOURCE,
} from 'config/constants';

/**
 * SDK exposing all available chains and we want to filter
 * those which we support - with frontend config (RPC, chainId, etc)
 */
type ActiveBridgeTargets = Extract<
  BridgeTarget | DepositSource,
  | 'stargate.avalanche'
  | 'stargate.arbitrum'
  | 'stargate.base'
  | 'stargate.berachain'
  | 'stargate.bnb'
  | 'stargate.ethereum'
  | 'stargate.optimism'
  // | 'stargate.polygon'
  // | 'stargate.sei'
  // | 'stargate.mantle'
  | 'katana.katana'
  // Special types for Claiming
  | typeof REWARDS_BRIDGE_SOURCE
  | typeof BANKROLL_BRIDGE_SOURCE
  | typeof POINTS_REWARD_BRIDGE_SOURCE
  | typeof COMPETITION_REWARD_BRIDGE_SOURCE
>;

const supportedChains = [
  'arbitrum',
  'avalanche',
  'baseEVM',
  'beraChain',
  // 'bnbSmartChain',
  'ethereum',
  'optimism',
  'katanaChain',
] satisfies ChainsFromConfig[];

type SupportedChains = (typeof supportedChains)[number];

function isSupportedChain(chain: ChainsFromConfig): chain is SupportedChains {
  return supportedChains.includes(chain as SupportedChains);
}

const fromApiTargetMap: {
  [bridge in ActiveBridgeTargets]: SupportedChains | undefined;
} = {
  'stargate.arbitrum': 'arbitrum',
  'stargate.avalanche': 'avalanche',
  // 'stargate.bnb': 'bnbSmartChain',
  'stargate.base': 'baseEVM',
  'stargate.ethereum': 'ethereum',
  'stargate.optimism': 'optimism',
  // 'stargate.polygon': 'polygonPoS',
  // 'stargate.sei': 'seiChain',
  // 'stargate.mantle': 'mantle',
  'stargate.berachain': 'beraChain',
  'katana.katana': 'katanaChain',
  [REWARDS_BRIDGE_SOURCE]: 'katanaChain',
  [BANKROLL_BRIDGE_SOURCE]: 'katanaChain',
  [POINTS_REWARD_BRIDGE_SOURCE]: 'katanaChain',
  [COMPETITION_REWARD_BRIDGE_SOURCE]: 'katanaChain',
};

/**
 * Generated as
  ```
  JSON.stringify(Object.entries(fromApiTargetMap).reduce((map, entry) => { map[entry[1]] = entry[0]; return map; }, {}));
  ```
 */
const toApiTargetMap: {
  [bridge in SupportedChains]: BridgeTarget | undefined;
} = {
  avalanche: 'stargate.avalanche',
  arbitrum: 'stargate.arbitrum',
  baseEVM: 'stargate.base',
  // bnbSmartChain: 'stargate.bnb',
  ethereum: 'stargate.ethereum',
  optimism: 'stargate.optimism',
  // polygonPoS: 'stargate.polygon',
  katanaChain: 'katana.katana',
  beraChain: 'stargate.berachain',
  // mantle: 'stargate.mantle',
  // seiChain: 'stargate.sei',
};

/** Throws error when not found (inside of `getBridgeTargetConfig`) */
export const getStargateBridgeConfig = (
  chain: ChainsFromConfig,
  devSandboxOverride?: boolean
) => {
  const sdkChain = convertChainToApiChainOrThrow(chain);
  const sdkChainConfig = getBridgeTargetConfig(
    sdkChain,
    devSandboxOverride ?? config.envIsSandbox
  );
  return {
    ...sdkChainConfig,
    sourceChainBridgingSpenderContract:
      'layerZeroVaultComposerSyncAddress' in sdkChainConfig
        ? sdkChainConfig.layerZeroVaultComposerSyncAddress
        : sdkChainConfig.layerzeroOFTAddress,
  };
};

export const isChainAvailableForStargateBridging = (
  chainKey: ChainsFromConfig
) => {
  const mainConfig = config.chains[chainKey];
  if (mainConfig.depositNotAvailable || mainConfig.depositPaused) {
    return false;
  }
  // TODO: katana verify that layerZeroVaultComposerSyncAddress is available for Ethereum
  // if(config.chains.katanaChain.stargateJumpChain === 'katanaChain')
  try {
    const stargateConfig = getStargateBridgeConfig(chainKey);
    return (
      stargateConfig.layerZeroEndpointId && stargateConfig.layerzeroOFTAddress
    );
    // return stargateConfig.isSupported && stargateConfig.isBridgeTarget;
  } catch (error) {
    console.warn(error?.message || error);
    return false;
  }
};

export const convertChainToApiChainOrThrow = (
  chain: ChainsFromConfig
): BridgeTarget => {
  if (!isSupportedChain(chain)) {
    throw new Error(`Chain ${chain} not supported`);
  }
  const targetChain = toApiTargetMap[chain];

  if (!targetChain) {
    console.error(`Unsupported withdrawal bridge target ${chain}`);
    throw new Error(`Unsupported withdrawal bridge target ${chain}`);
  }
  return targetChain;
};

export const convertApiBridge = (
  bridgeTarget: BridgeTarget | DepositSource
): {
  bridge?: 'Stargate';
  /** Optional targetChain (frontend) - active chains must be available, but some could be removed or not linked */
  targetChain?: ChainsFromConfig;
  /** Parsed chain name - always available. Should be only used when chain is not set in frontend config. */
  targetChainName: string;
} => {
  const targetChain = fromApiTargetMap[bridgeTarget as ActiveBridgeTargets];

  if (!targetChain) {
    console.error(`Unsupported bridge target ${bridgeTarget}`);
    // throw new Error(`Unsupported bridge target ${bridgeTarget}`);
  }
  const [bridgeName, chainName] = bridgeTarget.split('.') || [];

  return {
    bridge: bridgeName.toLowerCase().includes('stargate')
      ? 'Stargate'
      : undefined,
    targetChain,
    targetChainName: chainName,
  };
};
